/**
 * CostEstimateFooter — the "≈ Estimated <operation> cost: $0.53" line shown in the footer of every
 * confirmation surface that starts token-billed work (Figma 9944:22608). Renders nothing off the
 * SaaS platform, so callers can mount it unconditionally.
 *
 * ``operation`` / ``params`` feed ``POST billing/estimate`` through ``useCostEstimate``; ``label``
 * names the work in the sentence ("re-evaluation", "data generation", "model training", ...).
 * ``placeholder`` keeps the line on screen as "Estimated <label> cost: -" while ``params`` is still
 * null (a multi-step modal whose priced inputs come on a later step).
 */
import React from 'react';
import PropTypes from 'prop-types';
import { Text } from '@spark';

import { useCostEstimate } from '../hooks/useCostEstimate';
import '../billing.css';

const USD = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

/** "$0.53"; amounts under a cent still show as "<$0.01" so a priced action never reads as free. */
export function formatEstimatedUsd(usd) {
    const amount = Number(usd);
    if (!Number.isFinite(amount) || amount <= 0) return USD.format(0);
    return amount < 0.01 ? `<${USD.format(0.01)}` : USD.format(amount);
}

/** "Estimated data generation cost: -" — the inputs that set the price are not entered yet. */
export const costEstimatePlaceholderText = (label) => `Estimated ${label} cost: -`;

/** The sentence for an estimate response; ``null`` while there is nothing to say yet. */
export function costEstimateText({ label, estimate, loading, error }) {
    if (estimate?.unavailable_reason) return `Estimated ${label} cost: not available yet`;
    if (estimate) return `Estimated ${label} cost: ${formatEstimatedUsd(estimate.estimated_usd)}`;
    if (loading) return `Estimating ${label} cost…`;
    if (error) return `Estimated ${label} cost: unavailable`;
    return null;
}

/** The "≈" glyph from the Figma footer (node 9944:22609), drawn in the current text colour. */
export const EstimationIcon = ({ className = '' }) => (
    <svg
        className={className}
        width="16"
        height="16"
        viewBox="0 0 18.2857 18.2857"
        fill="none"
        aria-hidden="true"
        focusable="false"
    >
        <path
            d="M6.14653 10.1093C7.33659 10.2154 8.46392 10.8905 9.50702 11.7488C10.4953 12.5619 11.4 13.0576 12.2414 13.1327C13.0353 13.2034 13.8854 12.9077 14.8117 11.866C15.0214 11.6305 15.3829 11.6096 15.6186 11.8191C15.854 12.0287 15.8747 12.3892 15.6655 12.6249C14.5604 13.8677 13.3785 14.3812 12.1409 14.2711C10.9509 14.1651 9.8236 13.4899 8.78046 12.6316C7.792 11.8183 6.88647 11.3227 6.04497 11.2477C5.25121 11.177 4.40196 11.473 3.47577 12.5144C3.26608 12.7502 2.90469 12.7709 2.66885 12.5613C2.43313 12.3516 2.41123 11.9902 2.62086 11.7544C3.72605 10.5117 4.9089 9.99903 6.14653 10.1093Z"
            fill="currentColor"
        />
        <path
            d="M6.14653 4.01443C7.33661 4.12057 8.4639 4.79563 9.50702 5.65394C10.4953 6.46703 11.4001 6.96272 12.2414 7.03786C13.0352 7.10855 13.8854 6.81281 14.8117 5.77112C15.0214 5.53543 15.3828 5.51458 15.6186 5.72425C15.854 5.93394 15.8749 6.29435 15.6655 6.53005C14.5604 7.77283 13.3785 8.2863 12.1409 8.17626C10.9509 8.07019 9.82357 7.395 8.78046 6.53675C7.79198 5.7234 6.88648 5.22789 6.04497 5.15282C5.25115 5.08214 4.40204 5.37797 3.47577 6.41956C3.26611 6.65534 2.90469 6.67598 2.66885 6.46644C2.43303 6.25674 2.41119 5.89535 2.62086 5.65952C3.72611 4.41663 4.90882 3.90416 6.14653 4.01443Z"
            fill="currentColor"
        />
    </svg>
);

EstimationIcon.propTypes = { className: PropTypes.string };

/** The "$" glyph of the download footer (Figma 9952:24284), drawn in the current text colour. */
export const DollarIcon = ({ className = '' }) => (
    <svg className={className} width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
        <text
            x="8"
            y="12.5"
            textAnchor="middle"
            fontFamily="var(--font-family-platform, 'Figtree', sans-serif)"
            fontSize="13"
            fontWeight="600"
            fill="currentColor"
        >
            $
        </text>
    </svg>
);

DollarIcon.propTypes = { className: PropTypes.string };

/** The footer line for an estimate state a caller already holds (``useCostEstimate`` or its own quote). */
export function CostEstimateLine({ text, loading = false, icon = 'estimate', className = '' }) {
    if (!text) return null;
    const Icon = icon === 'dollar' ? DollarIcon : EstimationIcon;
    return (
        <div
            className={`cost-estimate${loading ? ' cost-estimate--loading' : ''} ${className}`.trim()}
            data-testid="cost-estimate"
            aria-live="polite"
        >
            <Icon className="cost-estimate__icon" />
            <Text as="span" variant="base-regular" color="primary" className="cost-estimate__text fs-unmask">
                {text}
            </Text>
        </div>
    );
}

CostEstimateLine.propTypes = {
    text: PropTypes.string,
    loading: PropTypes.bool,
    icon: PropTypes.oneOf(['estimate', 'dollar']),
    className: PropTypes.string,
};

export default function CostEstimateFooter({
    operation,
    params,
    label,
    enabled = true,
    refreshKey = 0,
    className = '',
    placeholder = false,
}) {
    const { estimate, loading, error, quoted, billable } = useCostEstimate(operation, params, {
        enabled,
        refreshKey,
    });
    if (!quoted) {
        if (!placeholder || !billable) return null;
        return <CostEstimateLine text={costEstimatePlaceholderText(label)} className={className} />;
    }
    return (
        <CostEstimateLine
            text={costEstimateText({ label, estimate, loading, error })}
            loading={loading}
            className={className}
        />
    );
}

CostEstimateFooter.propTypes = {
    operation: PropTypes.oneOf([
        'train',
        'revaluate',
        'data_generation',
        'label_generation',
        'data_retrieval',
        'model_download',
        'model_deployment',
    ]).isRequired,
    params: PropTypes.object,
    label: PropTypes.string.isRequired,
    enabled: PropTypes.bool,
    refreshKey: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    className: PropTypes.string,
    placeholder: PropTypes.bool,
};
